import { clearExtrasRecovery } from '@/features/home/composer/studio/useExtrasRecovery';
import { TooltipButton } from '@/ui/TooltipButton';
import { saveRecovery } from '@/features/home/composer/draftRecovery';
// shell/ShellLayout.tsx — the persistent app shell. Renders the fixed Backdrop (z-index 0) behind
// a CSS-grid of Rail (full height) / Topbar (row 1, center+right) / routed Outlet (center) /
// page-fed RightRail (right). Provides the RightRail publish context to the routed page.
//
// Responsive behaviour (spec §2):
//   desktop  (≥1024): 3-col grid — Rail + Topbar + feed + RightRail. Unchanged.
//   tablet   (768-1023): 2-col grid — Rail + Topbar + feed; right-rail column hidden.
//   mobile   (<768): single column — MobileTopBar + BottomTabBar replace Rail/Topbar;
//             page-fed right-rail content opens in a right-side Sheet drawer;
//             compose FAB opens a bottom Sheet wrapping <Composer>.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { authService } from '@/core/services/authService';
import styled from 'styled-components';
import { media } from '@/theme/media';
import { useViewport, useSession, usePreferences } from '@/hooks';
import { Icon } from '@/icons';
import { quietScrollbar } from '@/theme/scrollbar';
import { Sheet, Button } from '@/ui';
import type { ComposerProps } from '@/features/home/Composer';
import { MediaViewerProvider } from '@/components/MediaViewer';
import { Backdrop } from './Backdrop';
import { Rail } from './Rail';
import { Topbar } from './Topbar';
import { RightRail } from './RightRail';
import type { ShellContext } from './RightRail';
import { RailToolbarContext } from './RightRail/context';
import { RailHeader } from './RightRail/RailHeader';
import { RailHeaderButton } from './RightRail/RailHeader.styles';
import { useRailCollapsed, useRightRailCollapsed } from './useRailCollapsed';
import { RouteTransition } from './RouteTransition';
import { VerifyEmailBanner } from './VerifyEmailBanner';
import { TwoFactorGraceBanner } from './TwoFactorGraceBanner';
import { OnboardingRedirect } from './OnboardingRedirect';
import { MobileTopBar } from './Topbar/MobileTopBar';
import { BottomTabBar } from './BottomTabBar/BottomTabBar';
import { SignupCtaBar, SIGNUP_CTA_BAR_HEIGHT, MOBILE_SIGNUP_CTA_BAR_HEIGHT } from './SignupCtaBar';
import { useGlobalHydration } from '@/hooks';
import { useComposeSheetStore } from '@/core/stores/composeSheetStore';
import { useToast } from '@/ui';

// Publishes the shell's grid track widths as CSS custom properties so BOTH the
// Grid (layout) and the Backdrop sibling (gutter mask) read the SAME values.
const Shell = styled.div<{ $collapsed: boolean; $rightCollapsed: boolean; $landing?: boolean }>`
  --rail-w: ${({ $collapsed }) => ($collapsed ? '76px' : '240px')};
  --right-w: ${({ $rightCollapsed, $landing }) => ($rightCollapsed || $landing ? '0px' : '340px')};
  ${media.tabletDown`--right-w: 0px;`}
  &[data-reduce-motion='true'] *,
  &[data-reduce-motion='true'] *::before,
  &[data-reduce-motion='true'] *::after {
    transition: none !important;
  }
`;

// ── Desktop/tablet 3-col / 2-col grid ─────────────────────────────────────────

const Grid = styled.div`
  position: relative;
  z-index: 1;
  height: 100vh;
  display: grid;
  grid-template-columns: var(--rail-w) minmax(0, 1fr) var(--right-w);
  grid-template-rows: 60px 1fr;
  transition: grid-template-columns 260ms cubic-bezier(0.22, 0.8, 0.25, 1);
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// Right-rail aside: hidden on tablet/below at the CSS level (the JS branch also skips rendering
// on mobile, but this CSS rule keeps it out of flow on tablet too without a re-render).
const RightRailWrapper = styled.div`
  grid-column: 3;
  grid-row: 2;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  ${media.tabletDown`
    display: none;
  `}
`;

// Only the slim reopening handle (or tablet drawer trigger) sits outside the rail.
const RightRailToggle = styled(TooltipButton)`
  position: fixed;
  top: 68px;
  right: 10px;
  z-index: 2;
  display: grid;
  place-items: center;
  width: 32px;
  height: 36px;
  padding: 7px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.textMuted};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.color.surfaceRaised};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 3px;
  }
  span {
    display: flex;
    transition: transform 260ms ease;
  }
  &[aria-expanded='false'] span {
    transform: rotateY(180deg);
  }
`;

const Center = styled.main<{
  $guestBar: boolean;
  $toggleOverContent?: boolean;
  $landing?: boolean;
}>`
  grid-column: 2;
  grid-row: 2;
  /* Extra bottom padding when the guest SignupCtaBar is showing (fixed to the
     viewport bottom on desktop/tablet, see SignupCtaBar.tsx) so it never
     covers the last card in the feed. */
  padding: 16px 14px ${({ $guestBar }) => ($guestBar ? 16 + SIGNUP_CTA_BAR_HEIGHT : 16)}px;
  /* Reserve a narrow lane for the reopening control on every route. */
  padding-right: ${({ $toggleOverContent }) => ($toggleOverContent ? '64px' : '14px')};
  ${({ $landing }) => $landing && 'padding: 0;'}
  overflow-y: auto;
  min-height: 0;
  min-width: 0;
  /* Keep the backdrop visible through user-selected glass cards. */
  isolation: isolate;
  /* A visible, quiet scrollbar. It was hidden outright, which removed the only
     scroll-position affordance the reading column had. */
  ${quietScrollbar}
`;

// ── Mobile single-column layout ────────────────────────────────────────────────

const MobileShell = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  &[data-reduce-motion='true'] * {
    transition: none !important;
  }
`;

const MobileCenter = styled.main<{ $guestBar: boolean; $landing?: boolean }>`
  flex: 1;
  overflow: visible;
  min-height: 0;
  /* Base 64px already clears BottomTabBar; add the guest SignupCtaBar's own
     (mobile-specific, STACKED-layout) height on top of that so it never
     covers the last card in the feed either. See SignupCtaBar.tsx's
     MOBILE_SIGNUP_CTA_BAR_HEIGHT docstring for why mobile reserves more
     than the desktop/tablet single-row SIGNUP_CTA_BAR_HEIGHT. */
  padding: 12px 12px
    calc(
      ${({ $guestBar }) => ($guestBar ? 64 + MOBILE_SIGNUP_CTA_BAR_HEIGHT : 64)}px +
        env(safe-area-inset-bottom)
    );
  ${({ $landing }) => $landing && 'padding: 0 0 calc(64px + env(safe-area-inset-bottom));'}
  /* Sundown shows through on mobile too; the backdrop's column scrim spans the
     (narrow) viewport so text stays legible. (Was an opaque bgRoot occluder.) */
  isolation: isolate;
  /* A visible, quiet scrollbar. It was hidden outright, which removed the only
     scroll-position affordance the reading column had. */
  ${quietScrollbar}
`;

// ── Component ─────────────────────────────────────────────────────────────────

// Lazy import Composer to avoid pulling its deps into the initial bundle on desktop
// (still in the same chunk for now — split later if needed).
let ComposerModule: typeof import('@/features/home/Composer') | null = null;
function LazyComposer(props: ComposerProps) {
  const [Mod, setMod] = useState<typeof import('@/features/home/Composer') | null>(ComposerModule);
  useEffect(() => {
    if (Mod) return;
    void import('@/features/home/Composer').then((m) => {
      ComposerModule = m;
      setMod(m);
    });
  }, [Mod]);
  if (!Mod) return null;
  return <Mod.Composer overlay {...props} />;
}

const DiscardNotice = styled.div`
  padding: 12px;
  margin-bottom: 16px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surface};
  p {
    margin: 0 0 10px;
  }
  div {
    display: flex;
    gap: 8px;
  }
`;

export function ShellLayout() {
  useGlobalHydration();
  const [rightRail, setRightRail] = useState<ReactNode>(null);
  // A page borrowing the right column (useWideCenter) — hides the rail like a collapse, but
  // is never saved and has no reopen handle: the page gives the column back itself.
  const [wideCenter, setWideCenter] = useState(false);
  const [drawerToolbar, setDrawerToolbar] = useState<HTMLSpanElement | null>(null);
  const expandRailRef = useRef<HTMLButtonElement>(null);
  const collapseRailRef = useRef<HTMLButtonElement>(null);
  const collapsed = useRailCollapsed((s) => s.collapsed);
  const rightCollapsed = useRightRailCollapsed((s) => s.collapsed);
  const rightHidden = rightCollapsed || wideCenter;
  const toggleRightRail = useRightRailCollapsed((s) => s.toggle);
  function collapseRightRail() {
    toggleRightRail();
    requestAnimationFrame(() => expandRailRef.current?.focus());
  }
  function expandRightRail() {
    toggleRightRail();
    requestAnimationFrame(() => collapseRailRef.current?.focus());
  }
  const { prefs } = usePreferences();
  const context = useMemo<ShellContext>(() => ({ setRightRail, setWideCenter }), []);
  const toast = useToast();
  const viewport = useViewport();
  const { isAuthenticated, currentUser } = useSession();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // 1.24.60: a guest at "/" is on the landing page, which is itself one large
  // conversion surface ending in its own CTA — a second fixed bar would only
  // cover it. Every other guest page keeps the bar, /feed included.
  const showGuestBar = !isAuthenticated && pathname !== '/';
  const isLanding = !isAuthenticated && pathname === '/';

  const [panelsOpen, setPanelsOpen] = useState(false);
  // Lifted to a store (W8, 1.21.00 review) so a distant component
  // (PostActions.handleQuote, on mobile viewport) can open this sheet too —
  // see composeSheetStore.ts's docstring.
  const composeOpen = useComposeSheetStore((s) => s.open);
  const openComposeSheet = useComposeSheetStore((s) => s.openSheet);
  const closeComposeSheet = useComposeSheetStore((s) => s.closeSheet);
  const [composeState, setComposeState] = useState({ dirty: false, busy: false });
  const [discardOpen, setDiscardOpen] = useState(false);
  function requestCloseCompose() {
    if (composeState.busy) return;
    if (composeState.dirty) setDiscardOpen(true);
    else closeComposeSheet();
  }
  function finishCompose() {
    setDiscardOpen(false);
    setComposeState({ dirty: false, busy: false });
    closeComposeSheet();
  }
  const composeContents = (
    <>
      {discardOpen && (
        <DiscardNotice role="alert">
          <p>Discard this post? Save it as a draft below to keep it.</p>
          <div>
            <Button onClick={() => setDiscardOpen(false)}>Keep writing</Button>
            <Button
              variant="danger"
              onClick={() => {
                saveRecovery(currentUser?.id ?? 'guest', '');
                clearExtrasRecovery(currentUser?.id ?? 'guest');
                finishCompose();
              }}
            >
              Discard post
            </Button>
          </div>
        </DiscardNotice>
      )}
      <LazyComposer
        key={currentUser?.id ?? 'guest'}
        recoverText
        onStateChange={setComposeState}
        onComplete={finishCompose}
      />
    </>
  );

  useEffect(() => {
    let last = 0;
    function onGated() {
      const now = Date.now();
      if (now - last < 4000) return; // throttle duplicate bursts
      last = now;
      toast('Verify your email to post or interact');
    }
    window.addEventListener('trux:email-unverified', onGated);
    return () => window.removeEventListener('trux:email-unverified', onGated);
  }, [toast]);

  // The server stopped recognising this browser (see apiClient). For a guest that is
  // just a signed-out request; for someone the app still shows as signed in it means
  // the session ended, so say so and take them to sign in, coming back to this page.
  useEffect(() => {
    function onExpired() {
      if (!isAuthenticated) return;
      authService.endLocalSession();
      toast('Your session ended. Sign in again to carry on.');
      const here = `${window.location.pathname}${window.location.search}`;
      void navigate(`/signin?next=${encodeURIComponent(here)}`);
    }
    window.addEventListener('trux:session-expired', onExpired);
    return () => window.removeEventListener('trux:session-expired', onExpired);
  }, [isAuthenticated, navigate, toast]);

  // ── Mobile layout ────────────────────────────────────────────────────────────
  const shellContents =
    viewport === 'mobile' ? (
      <>
        <Backdrop />
        <MobileShell data-reduce-motion={prefs.reduceMotion}>
          <MobileTopBar onOpenPanels={() => setPanelsOpen(true)} hasPanels={rightRail != null} />
          <MobileCenter $guestBar={showGuestBar} $landing={isLanding} data-testid="mobile-center">
            <OnboardingRedirect />
            <TwoFactorGraceBanner />
            <RouteTransition>
              <Outlet context={context} />
            </RouteTransition>
          </MobileCenter>
          <VerifyEmailBanner />
          {/* Fix-round item 28: DOM order before BottomTabBar so keyboard tab order
              matches visual stacking (SignupCtaBar sits ABOVE BottomTabBar on screen —
              position: fixed, so its DOM position here has no layout effect). */}
          {showGuestBar && <SignupCtaBar />}
          <BottomTabBar onCompose={openComposeSheet} />
        </MobileShell>
      </>
    ) : (
      <Shell
        $collapsed={collapsed}
        $rightCollapsed={rightHidden}
        $landing={isLanding}
        data-reduce-motion={prefs.reduceMotion}
      >
        <Backdrop />
        <Grid>
          <Rail />
          <Topbar onCompose={openComposeSheet} />
          <Center
            $guestBar={showGuestBar}
            $landing={isLanding}
            $toggleOverContent={
              (rightCollapsed || viewport === 'tablet') && rightRail != null && !wideCenter
            }
            data-testid="feed-center"
          >
            <OnboardingRedirect />
            <TwoFactorGraceBanner />
            <RouteTransition>
              <Outlet context={context} />
            </RouteTransition>
          </Center>
          <RightRailWrapper>
            {viewport === 'desktop' && (
              <RightRail
                collapsed={rightHidden}
                hasToggle={rightRail != null}
                onCollapse={collapseRightRail}
                collapseRef={collapseRailRef}
              >
                {rightRail}
              </RightRail>
            )}
          </RightRailWrapper>
          {rightRail != null && !wideCenter && (viewport === 'tablet' || rightCollapsed) && (
            <RightRailToggle
              ref={expandRailRef}
              type="button"
              aria-controls={viewport === 'desktop' ? 'right-sidebar' : undefined}
              aria-haspopup={viewport === 'tablet' ? 'dialog' : undefined}
              aria-expanded={viewport === 'tablet' ? panelsOpen : !rightCollapsed}
              aria-label={
                viewport === 'tablet'
                  ? 'Open panels'
                  : rightCollapsed
                    ? 'Expand right sidebar'
                    : 'Collapse right sidebar'
              }
              title={
                viewport === 'tablet'
                  ? 'Open panels'
                  : rightCollapsed
                    ? 'Expand right sidebar'
                    : 'Collapse right sidebar'
              }
              onClick={viewport === 'tablet' ? () => setPanelsOpen(true) : expandRightRail}
            >
              <span>
                <Icon name="panel-right" size={20} />
              </span>
            </RightRailToggle>
          )}
        </Grid>
        <VerifyEmailBanner />

        {showGuestBar && <SignupCtaBar />}
      </Shell>
    );

  return (
    <MediaViewerProvider>
      {shellContents}
      {viewport !== 'desktop' && (
        <Sheet
          side="right"
          open={panelsOpen}
          onClose={() => setPanelsOpen(false)}
          title="Panels"
          renderHeader={(titleId) => (
            <RailHeader titleId={titleId}>
              <span ref={setDrawerToolbar} />
              <RailHeaderButton
                type="button"
                aria-label="Close panels"
                title="Close panels"
                onClick={() => setPanelsOpen(false)}
              >
                <Icon name="panel-right" size={18} />
              </RailHeaderButton>
            </RailHeader>
          )}
        >
          <RailToolbarContext.Provider value={drawerToolbar}>
            {rightRail}
          </RailToolbarContext.Provider>
        </Sheet>
      )}
      {/* Keep the composer in the same tree position across breakpoints. The sheet's
          CSS changes its placement without discarding text, media or an in-flight save. */}
      <Sheet
        side="center"
        open={composeOpen}
        onClose={requestCloseCompose}
        title="Create a post"
        width="min(1040px, calc(100vw - 48px))"
        closeLabel="Close composer"
      >
        {composeContents}
      </Sheet>
    </MediaViewerProvider>
  );
}
